import { useState } from 'react';
import { BACKEND_URL } from '@/utils/api';
import { CheckCircle2, ChevronLeft, ChevronRight, Maximize2, Minimize2, Lock, Award } from 'lucide-react';

const SKILL_IMAGES = {
  'Blow Bubbles': 'https://images.unsplash.com/photo-1560347876-aeef00ee58a1?w=400&h=400&fit=crop',
  'Kick Legs': 'https://images.unsplash.com/photo-1530549387789-4c1017266635?w=400&h=400&fit=crop',
  'Float on Back': 'https://images.unsplash.com/photo-1519315901367-f34ff9154487?w=400&h=400&fit=crop',
  'Arm Strokes': 'https://images.unsplash.com/photo-1571019614242-c5c5dee9f50b?w=400&h=400&fit=crop',
  'Submerge Face': 'https://images.unsplash.com/photo-1682687218608-5e2522b04673?w=400&h=400&fit=crop',
};

function getSkillImage(skill) {
  // Match SessionPage logic: check skill.image first, then skill.image_url
  if (skill.image) {
    if (skill.image_is_url || skill.image.startsWith('http')) return skill.image;
    return `${BACKEND_URL}/api/uploads/${skill.image}`;
  }
  if (skill.image_url) {
    return skill.image_url.startsWith('http') ? skill.image_url : `${BACKEND_URL}/api/uploads/${skill.image_url}`;
  }
  return SKILL_IMAGES[skill.name] || `https://ui-avatars.com/api/?name=${encodeURIComponent(skill.name)}&size=400&background=ECFEFF&color=0E7490&bold=true&font-size=0.3`;
}

export function SessionFullScreen({ session, onComplete, onUncomplete, onExit }) {
  const skills = session?.skills || [];
  const [activeIndex, setActiveIndex] = useState(() => {
    const first = skills.findIndex(s => !s.completed);
    return first >= 0 ? first : 0;
  });
  const [tapped, setTapped] = useState(null);
  const [completing, setCompleting] = useState(false);

  const activeSkill = skills[activeIndex];
  const completedCount = skills.filter(s => s.completed).length;
  const allDone = completedCount === skills.length;

  const rewardImgSrc = session?.reward_image
    ? (session.reward_image.startsWith('http') ? session.reward_image : `${BACKEND_URL}/api/uploads/${session.reward_image}`)
    : null;

  const handleSkillTap = async (skill, index) => {
    if (skill.completed) return;
    setTapped(index);
  };

  const handleFullScreenTap = async () => {
    if (tapped === null || completing) return;
    setCompleting(true);
    const skill = skills[tapped];
    await onComplete(skill.skill_id);
    setCompleting(false);
    setTapped(null);
    // Auto-advance to next incomplete
    const nextIdx = skills.findIndex((s, i) => i > tapped && !s.completed);
    if (nextIdx >= 0) setActiveIndex(nextIdx);
  };

  // Full screen skill tap overlay
  if (tapped !== null) {
    const skill = skills[tapped];
    return (
      <div
        className="fixed inset-0 z-[9999] bg-[#1E1B4B] flex flex-col items-center justify-center cursor-pointer select-none"
        onClick={handleFullScreenTap}
        data-testid="skill-fullscreen-tap"
      >
        <img
          src={getSkillImage(skill)}
          alt={skill.name}
          className={`w-[70vmin] h-[70vmin] object-cover rounded-[2rem] shadow-2xl border-4 border-[#22D3EE] ${completing ? 'scale-95 opacity-50' : 'animate-pulse'}`}
          style={{ transition: 'all 0.3s' }}
        />
        <p className="text-white text-3xl sm:text-5xl font-extrabold mt-6 text-center px-4" style={{ fontFamily: 'Nunito' }}>
          {skill.name}
        </p>
        <p className="text-[#22D3EE] text-lg sm:text-2xl font-bold mt-3">
          {completing ? 'Great job!' : 'Tap to complete!'}
        </p>
        <button
          onClick={(e) => { e.stopPropagation(); setTapped(null); }}
          className="absolute top-6 right-6 text-white/60 hover:text-white p-3"
          data-testid="close-fullscreen-tap"
        >
          <Minimize2 className="w-8 h-8" />
        </button>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-[9998] bg-gradient-to-b from-[#0F172A] to-[#1E1B4B] flex flex-col overflow-hidden select-none touch-none" data-testid="session-fullscreen">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 bg-black/20 shrink-0">
        <button onClick={onExit} className="flex items-center gap-2 text-white/70 hover:text-white font-bold text-sm" data-testid="exit-fullscreen">
          <Minimize2 className="w-5 h-5" /> Exit Full Screen
        </button>
        <div className="text-center">
          <p className="text-white font-extrabold text-lg" style={{ fontFamily: 'Nunito' }}>
            Session #{session?.session_number || '?'}
          </p>
        </div>
        <div className="text-[#22D3EE] font-bold text-sm">
          {completedCount}/{skills.length}
        </div>
      </div>

      {/* Progress Bar */}
      <div className="w-full h-2 bg-white/10 shrink-0">
        <div
          className="h-2 bg-[#22D3EE] rounded-r-full transition-all duration-500"
          style={{ width: `${skills.length > 0 ? (completedCount / skills.length) * 100 : 0}%` }}
        />
      </div>

      {/* Main Content */}
      <div className="flex-1 flex flex-col items-center justify-center px-4 overflow-hidden">
        {allDone ? (
          <div className="text-center animate-fadeIn">
            {rewardImgSrc ? (
              <img src={rewardImgSrc} alt="Reward" className="w-[40vmin] h-[40vmin] object-cover rounded-[2rem] shadow-2xl border-4 border-[#C4F017] mx-auto mb-4 animate-pulse" />
            ) : (
              <div className="text-7xl mb-6">&#127942;</div>
            )}
            <p className="text-white text-4xl sm:text-5xl font-extrabold" style={{ fontFamily: 'Nunito' }}>All Done!</p>
            {session?.then_reward && (
              <p className="text-[#C4F017] text-xl sm:text-2xl font-bold mt-3">{session.then_reward}</p>
            )}
            <p className="text-[#22D3EE] text-lg mt-2">Amazing work today!</p>
            <button onClick={onExit} className="mt-8 px-8 py-3 bg-[#22D3EE] text-[#1E1B4B] font-extrabold rounded-full text-lg" data-testid="done-exit-btn">
              Back to Session
            </button>
          </div>
        ) : (
          <>
            {/* Active Skill - Big */}
            {activeSkill && !activeSkill.completed && (
              <div
                className="cursor-pointer flex flex-col items-center animate-fadeIn"
                onClick={() => handleSkillTap(activeSkill, activeIndex)}
                data-testid={`fullscreen-skill-${activeSkill.skill_id}`}
              >
                <img
                  src={getSkillImage(activeSkill)}
                  alt={activeSkill.name}
                  className="w-[55vmin] h-[55vmin] object-cover rounded-[2rem] shadow-2xl border-4 border-[#22D3EE] hover:scale-105 transition-transform"
                />
                <p className="text-white text-3xl sm:text-4xl font-extrabold mt-5 text-center" style={{ fontFamily: 'Nunito' }}>
                  {activeSkill.name}
                </p>
                <p className="text-[#22D3EE]/70 text-base mt-2 font-semibold">Tap to begin</p>
              </div>
            )}
          </>
        )}
      </div>

      {/* Bottom Skill Strip */}
      <div className="shrink-0 bg-black/30 px-3 py-3">
        <div className="flex gap-3 overflow-x-auto pb-1 justify-center">
          {skills.map((skill, i) => (
            <button
              key={skill.skill_id}
              onClick={() => {
                if (skill.completed && onUncomplete) {
                  onUncomplete(skill);
                } else if (!skill.completed) {
                  setActiveIndex(i);
                }
              }}
              className={`shrink-0 flex flex-col items-center gap-1 transition-all ${skill.completed ? 'opacity-50 hover:opacity-80' : i === activeIndex ? 'scale-110' : 'opacity-60 hover:opacity-90'}`}
              data-testid={`strip-skill-${skill.skill_id}`}
            >
              <div className="relative">
                <img
                  src={getSkillImage(skill)}
                  alt={skill.name}
                  className={`w-14 h-14 sm:w-16 sm:h-16 object-cover rounded-xl ${i === activeIndex && !skill.completed ? 'border-2 border-[#22D3EE] shadow-lg' : 'border border-white/20'}`}
                />
                {skill.completed && (
                  <div className="absolute inset-0 bg-green-500/60 rounded-xl flex items-center justify-center group hover:bg-red-500/60">
                    <CheckCircle2 className="w-6 h-6 text-white" />
                  </div>
                )}
              </div>
              <span className="text-white/80 text-[10px] font-bold max-w-[64px] truncate">{skill.name}</span>
            </button>
          ))}
          {/* Reward preview with lock */}
          {(rewardImgSrc || session?.then_reward) && (
            <div className="shrink-0 flex flex-col items-center gap-1" data-testid="strip-reward">
              <div className="relative">
                {rewardImgSrc ? (
                  <img src={rewardImgSrc} alt="Reward" className={`w-14 h-14 sm:w-16 sm:h-16 object-cover rounded-xl border-2 ${allDone ? 'border-[#C4F017] shadow-lg shadow-[#C4F017]/30' : 'border-white/20 grayscale'}`} />
                ) : (
                  <div className={`w-14 h-14 sm:w-16 sm:h-16 rounded-xl flex items-center justify-center ${allDone ? 'bg-[#C4F017]/30 border-2 border-[#C4F017]' : 'bg-white/10 border border-white/20'}`}>
                    <Award className={`w-7 h-7 ${allDone ? 'text-[#C4F017]' : 'text-white/40'}`} />
                  </div>
                )}
                {!allDone && (
                  <div className="absolute inset-0 bg-black/50 rounded-xl flex items-center justify-center">
                    <Lock className="w-5 h-5 text-white/80" />
                  </div>
                )}
              </div>
              <span className={`text-[10px] font-bold max-w-[64px] truncate ${allDone ? 'text-[#C4F017]' : 'text-white/40'}`}>Reward</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
